// client/src/pages/Store.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, AlertCircle, CheckCircle, XCircle, Loader2, Heart, Package } from 'lucide-react';
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import storeService, { Product, Pagination } from '@/api/services/storeService';


const CATEGORY_MAP: Record<string, string> = {
  all:         '',
  indoor:      'indoor_kits',
  outdoor:     'outdoor_kits',
  accessories: 'accessories',
  network:     'network_solutions',
  support:     'support',
};

const HotspotStorePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();


  const [currentPage, setCurrentPage]         = useState(1);
  const [limit, setLimit]                     = useState(12);
  const [activeCategory, setActiveCategory]   = useState('all');
  const [products, setProducts]               = useState<Product[]>([]);
  const [pagination, setPagination]           = useState<Pagination | null>(null);
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState<string | null>(null);
  const [addingToCart, setAddingToCart]       = useState<string | null>(null);
  const [message, setMessage]                 = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // wishlist: tracks which product IDs are being toggled and which are wishlisted
  const [wishlistLoading, setWishlistLoading] = useState<Set<string>>(new Set());
  const [wishlisted, setWishlisted]           = useState<Record<string, boolean>>({});

  // ── Fetch products whenever page, category, or limit changes ──────────────
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await storeService.getAllProducts({
        category: CATEGORY_MAP[activeCategory],
        page: currentPage,
        limit,
        sortBy: 'newest',
      });
      setProducts(result.products);
      setPagination(result.pagination);

      // Seed wishlist state from the product's isWishlisted flag returned by the API
      const initial: Record<string, boolean> = {};
      result.products.forEach(p => { initial[p.id] = p.isWishlisted; });
      setWishlisted(prev => ({ ...prev, ...initial }));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error?.response?.data?.message ?? 'Failed to load products. Please try again.');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [activeCategory, currentPage, limit]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleCategoryChange = (categoryId: string) => {
    setActiveCategory(categoryId);
    setCurrentPage(1);
  };

  // ── Add to cart ───────────────────────────────────────────────────────────
  const handleAddToCart = async (productId: string) => {
    setAddingToCart(productId);
    try {
      await storeService.addToCart({ productId, quantity: 1 });
      showMessage('success', 'Product added to cart successfully!');
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      showMessage('error', error?.response?.data?.message ?? 'Failed to add product to cart.');
    } finally {
      setAddingToCart(null);
    }
  };

  // ── Toggle wishlist ───────────────────────────────────────────────────────
  const handleToggleWishlist = async (product: Product) => {
    window.dispatchEvent(new Event('wishlistUpdated'));
    const productId = product.id;
    setWishlistLoading(prev => new Set(prev).add(productId));
    try {
      if (wishlisted[productId]) {
        // Find the wishlist entry id by fetching the wishlist and matching productId
        const { items } = await storeService.getWishlist();
        const entry = items.find(i => i.productId === productId);
        if (entry) {
          await storeService.removeFromWishlist(entry.id);
        }
        setWishlisted(prev => ({ ...prev, [productId]: false }));
        showMessage('success', t('operator.storePage.wishlist.removed', {
          defaultValue: '{{name}} removed from wishlist.',
          name: product.name,
        }));
      } else {
        await storeService.addToWishlist(productId);
        setWishlisted(prev => ({ ...prev, [productId]: true }));
        showMessage('success', t('operator.storePage.wishlist.added', {
          defaultValue: '{{name}} added to wishlist.',
          name: product.name,
        }));
      }
    } catch (err: unknown) {
      const error = err as { response?: { status?: number, data?: { error?: string } } };
      const alreadyIn = error?.response?.status === 409;
      if (alreadyIn) {
        // product is already wishlisted — sync local state silently
        setWishlisted(prev => ({ ...prev, [productId]: true }));
      } else {
        showMessage('error', error?.response?.data?.error ?? 'Failed to update wishlist.');
      }
    } finally {
      setWishlistLoading(prev => { const s = new Set(prev); s.delete(productId); return s; });
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const categories = [
    { id: 'all',         label: t('operator.storePage.categories.all',         { defaultValue: 'All Products' }) },
    { id: 'indoor',      label: t('operator.storePage.categories.indoor',      { defaultValue: 'Indoor Kits' }) },
    { id: 'outdoor',     label: t('operator.storePage.categories.outdoor',     { defaultValue: 'Outdoor Kits' }) },
    { id: 'accessories', label: t('operator.storePage.categories.accessories', { defaultValue: 'Accessories' }) },
    { id: 'network',     label: t('operator.storePage.categories.network',     { defaultValue: 'Network Solutions' }) },
    { id: 'support',     label: t('operator.storePage.categories.support',     { defaultValue: 'Support' }) },
  ];

  // ── Helpers ───────────────────────────────────────────────────────────────
  const isAvailable = (p: Product) =>
    p.stockAvailable !== undefined ? p.stockAvailable : p.stockStatus === 'in_stock';

  const totalPages = pagination?.totalPages ?? 1;

  return (
    <>
      <Helmet>
        <title>{t('operator.commerce.store.meta.title', { defaultValue: 'Hotspot Store | KonectX' })}</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-black px-3 sm:px-4 lg:px-8 py-4 sm:py-6">

        {/* Toast */}
        {message && (
          <div className="max-w-7xl mx-auto mb-4">
            <div className={`p-3 sm:p-4 rounded-lg flex items-start sm:items-center gap-2 ${
              message.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
            }`}>
              {message.type === 'success'
                ? <CheckCircle className="w-5 h-5 flex-shrink-0" />
                : <XCircle    className="w-5 h-5 flex-shrink-0" />}
              <span className="text-xs sm:text-sm">{message.text}</span>
            </div>
          </div>
        )}

        {/* Header + Category Nav */}
        <div className="max-w-7xl mx-auto mb-6">
          <div className="flex items-center justify-between gap-3 mb-3 sm:mb-4">
            <span className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-200">
              {t('operator.commerce.store.title', { defaultValue: 'Store' })}
            </span>
            <button
              onClick={() => navigate('/operator/orders')}
              className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg bg-black text-white dark:bg-blue-600 dark:text-black text-xs sm:text-sm font-medium hover:bg-gray-800 dark:hover:bg-blue-500 transition-colors"
            >
              <Package className="w-4 h-4" />
              <span>{t('operator.commerce.orders.title', { defaultValue: 'My Orders' })}</span>
            </button>
          </div>

          <nav className="mt-2 sm:mt-4 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0">
            <div className="flex gap-2 sm:gap-3 min-w-max sm:min-w-0 sm:flex-wrap">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryChange(category.id)}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-all whitespace-nowrap text-xs sm:text-sm ${
                    activeCategory === category.id
                      ? 'bg-black dark:bg-blue-600 text-white shadow-md'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800'
                  }`}
                >
                  {category.label}
                  {category.id !== 'all' && activeCategory === category.id && pagination && (
                    <span className="ml-1.5 sm:ml-2 text-xs bg-white/20 px-1.5 sm:px-2 py-0.5 rounded-full">
                      {pagination.total}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </nav>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto pb-8 sm:pb-12">
          <main className="w-full">

            {/* Toolbar */}
            <div className="rounded-lg p-2 mb-4 sm:mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading products...
                  </span>
                ) : (
                  <span>
                    {t('operator.commerce.store.showingProducts', {
                      defaultValue: 'Showing {{start}}–{{end}} of {{count}} products',
                      start: products.length > 0 ? ((currentPage - 1) * limit) + 1 : 0,
                      end:   ((currentPage - 1) * limit) + products.length,
                      count: pagination?.total ?? 0,
                    })}
                  </span>
                )}
              </div>

              <select
                value={limit}
                onChange={(e) => { setLimit(Number(e.target.value)); setCurrentPage(1); }}
                className="border border-gray-300 dark:bg-black dark:text-blue-700 dark:border-blue-700 rounded px-2 sm:px-3 py-1.5 sm:py-1 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={12}>{t('operator.storePage.perPage.12', { defaultValue: '12 per page' })}</option>
                <option value={24}>{t('operator.storePage.perPage.24', { defaultValue: '24 per page' })}</option>
                <option value={48}>{t('operator.storePage.perPage.48', { defaultValue: '48 per page' })}</option>
              </select>
            </div>

            {/* Loading skeleton */}
            {loading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
                {[...Array(limit > 6 ? 6 : limit)].map((_, i) => (
                  <div key={i} className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden animate-pulse">
                    <div className="aspect-square bg-gray-200 dark:bg-gray-700" />
                    <div className="p-4 space-y-3">
                      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                      <div className="h-9 bg-gray-200 dark:bg-gray-700 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Error state */}
            {!loading && error && (
              <div className="text-center py-12 px-4">
                <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">Something went wrong</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{error}</p>
                <button
                  onClick={fetchProducts}
                  className="px-5 py-2 bg-black dark:bg-blue-600 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-blue-700 text-sm transition-colors"
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Empty state */}
            {!loading && !error && products.length === 0 && (
              <div className="text-center py-12 sm:py-16 px-4">
                <AlertCircle className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg sm:text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  {t('operator.commerce.store.emptyTitle', { defaultValue: 'No products found' })}
                </h3>
                <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
                  {t('operator.commerce.store.emptyBody', { defaultValue: 'No products match the selected category.' })}
                </p>
                <button
                  onClick={() => handleCategoryChange('all')}
                  className="px-5 sm:px-6 py-2 sm:py-2.5 bg-black dark:bg-blue-600 text-white rounded-lg text-sm sm:text-base transition-colors"
                >
                  {t('operator.commerce.store.viewAllProducts', { defaultValue: 'View All Products' })}
                </button>
              </div>
            )}

            {/* Products Grid */}
            {!loading && !error && products.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
                {products.map((product) => {
                  const available        = isAvailable(product);
                  const isWishlisted     = wishlisted[product.id] ?? product.isWishlisted;
                  const isWishlistBusy   = wishlistLoading.has(product.id);

                  return (
                    <div
                      key={product.id}
                      className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden hover:shadow-lg transition-shadow"
                    >
                      {/* Image — wishlist button overlaid top-right */}
                      <div
                        className="relative aspect-square bg-gray-100 dark:bg-gray-800 flex items-center justify-center p-4 sm:p-6 cursor-pointer"
                        onClick={() => navigate(`/operator/store/product/${product.id}`)}
                      >
                        <img
                          src={product.thumbnailUrl}
                          alt={product.name}
                          className="max-w-full max-h-full object-contain"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              'https://placehold.co/300x300/1a1a2e/60a5fa?text=KonectX';
                          }}
                        />

                        {/* ── Wishlist toggle button ── */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // prevent navigating to product detail
                            handleToggleWishlist(product);
                          }}
                          disabled={isWishlistBusy}
                          aria-label={
                            isWishlisted
                              ? t('operator.storePage.wishlist.removeAriaLabel', { defaultValue: 'Remove from wishlist' })
                              : t('operator.storePage.wishlist.addAriaLabel',    { defaultValue: 'Add to wishlist' })
                          }
                          className={`absolute top-2.5 right-2.5 z-10 p-2 rounded-full backdrop-blur-sm shadow-sm transition-all
                            ${isWishlisted
                              ? 'bg-rose-50 dark:bg-rose-900/40 text-rose-500 hover:bg-rose-100 dark:hover:bg-rose-900/60'
                              : 'bg-white/80 dark:bg-gray-900/80 text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {isWishlistBusy ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Heart
                              className={`w-4 h-4 transition-transform active:scale-90 ${isWishlisted ? 'fill-rose-500' : ''}`}
                            />
                          )}
                        </button>
                      </div>

                      {/* Info */}
                      <div className="p-3 sm:p-4 dark:bg-[#333436]">
                        <button
                          onClick={() => navigate(`/operator/store/product/${product.id}`)}
                          className="text-left font-bold text-base sm:text-lg lg:text-xl text-gray-800 dark:text-gray-300 mb-1 sm:mb-2 hover:text-blue-600 dark:hover:text-blue-400 transition-colors line-clamp-2 w-full"
                        >
                          {product.name}
                        </button>

                        {/* Stars */}
                        <div className="flex items-center space-x-1 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <span key={i} className={`text-base sm:text-lg ${i < Math.floor(product.rating) ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'}`}>★</span>
                          ))}
                          <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 ml-1">({product.reviewCount})</span>
                        </div>

                        <p className="text-xs sm:text-sm text-black dark:text-gray-400 mb-3 line-clamp-2">
                          {product.shortDescription}
                        </p>

                        <div className="flex items-center justify-between mb-3">
                          <span className="text-lg sm:text-xl font-bold text-gray-800 dark:text-gray-300">
                            {product.currency ?? '$'}{product.price}
                          </span>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            available
                              ? 'text-green-600 bg-green-50 dark:bg-green-900/20'
                              : 'text-red-600 bg-red-50 dark:bg-red-900/20'
                          }`}>
                            {available
                              ? t('operator.commerce.common.inStock',    { defaultValue: 'In Stock' })
                              : t('operator.commerce.common.outOfStock', { defaultValue: 'Out of Stock' })}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-2 sm:gap-3">
                          <button
                            onClick={() => handleAddToCart(product.id)}
                            disabled={!available || addingToCart === product.id}
                            className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium bg-gray-900 dark:bg-blue-700 text-white rounded-lg sm:rounded-xl hover:bg-gray-800 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className="inline-flex items-center justify-center gap-1 sm:gap-1.5">
                              {addingToCart === product.id ? (
                                <><Loader2 className="w-3.5 h-3.5 animate-spin" /><span>{t('operator.storePage.actions.adding', { defaultValue: 'Adding...' })}</span></>
                              ) : (
                                <><ShoppingCart className="w-3.5 h-3.5" /><span>{t('operator.storePage.actions.addToCart', { defaultValue: 'Add to Cart' })}</span></>
                              )}
                            </span>
                          </button>

                          <button
                            onClick={() => navigate(`/operator/store/product/${product.id}`)}
                            className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium border border-gray-300 dark:border-blue-700 text-gray-800 dark:text-blue-300 rounded-lg sm:rounded-xl hover:bg-gray-50 dark:hover:bg-blue-900/30 transition-colors"
                          >
                            {t('operator.commerce.common.viewMore', { defaultValue: 'View More' })}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {!loading && !error && totalPages > 1 && (
              <div className="mt-6 sm:mt-8 flex items-center justify-center">
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                    className="px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-800 text-xs sm:text-sm disabled:opacity-40"
                  >
                    {t('operator.commerce.common.previous', { defaultValue: 'Previous' })}
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-2 sm:px-3 py-1.5 sm:py-2 border rounded text-xs sm:text-sm ${
                        page === currentPage
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                    className="px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-800 text-xs sm:text-sm disabled:opacity-40"
                  >
                    {t('operator.commerce.common.next', { defaultValue: 'Next' })}
                  </button>
                </div>
              </div>
            )}
          </main>
        </div>

        {/* Footer — unchanged */}
        {/* ... your existing footer JSX ... */}
      </div>
    </>
  );
};

export default HotspotStorePage;
